/*
** $Id$
**
** Copyright 2018 Port25 Solutions, Inc.
** All rights reserved.
*/


import React, { Component } from 'react';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer,
         Legend, Tooltip, CartesianGrid } from 'recharts';
import '../../css/chart.css';
import PropTypes from 'prop-types';


/**
 * A chart to show current traffic.
 */
export default class TrafficChart extends Component {
    static propTypes = {
        /**
         * A list of inbound and outbound traffic counts over time. 
         * 
         * Each object in the array should be in the format:
         *      { time: <time>, inRcp: <inRcp>, outRcp: <outRcp> }
         * where <inRcp> and <outRcp> are inbound and outbound counts for
         * the given time <time>, where counts are integers and time is
         * in ISO format.
         */
        trafficOverTime: PropTypes.array.isRequired
    };


    /**
     * Formats X-Axis tick label for the given date and time.
     * 
     * @param dateTime
     *      date and time to format in ISO format.  Note that the ISO format
     *      does not strictly need 'T' as a separator between date and time. 
     *      And also it does not strictly need a colon in the time zone
     *      specifier.  So these are all valid as inputs for this function:
     *          2018-08-23T10:55:29Z
     *          2018-08-23T10:55:29-05:00 
     *          2018-08-23T10:55:29-0500 
     *          2018-08-23 10:55:29-05:00 
     *          2018-08-23 10:55:29-0500 
     *          2018-08-23 10:55:29Z
     *      @see https://en.wikipedia.org/wiki/ISO_8601
     */
    tickFormatter(dateTime) {
        // We do not try to parse the date as 'Date' objects since we have
        // to be extra cautious about the format being valid on all supported
        // browsers.  For example, Safari doesn't like the format:
        // "2018-08-24 00:00:05-0500" while FireFox and Chrome are ok with it.
        // @see https://stackoverflow.com/questions/50696939/new-date-returning-invalid-date-safari
        if (!dateTime) {
            return '' // when the chart is empty
        }
        const TIME_LENGTH = 8   // hh:mm:ss
        let dateTimeSep = dateTime.indexOf('T') !== -1 ? 'T' : ' '
        let time = dateTime.split(dateTimeSep)[1]
        return time.substring(time, TIME_LENGTH)
    }


    render() {
        // Recharts documentation isn't clear what units minTickGap uses.
        // We use 50 since it provides about 10 ticks in the full-screen browser
        // view with the current layout.  We prefer using minTickGap instead of
        // "interval" since it automatically takes care of making the labels
        // responsive (e.g. hides labels when they don't fit in the screen).
        // http://recharts.org/en-US/api/XAxis#minTickGap
        let xTickGap = 50

        // ResponsiveContainer needs either a fixed height or a width.
        // @see https://stackoverflow.com/questions/47614196/rechart-responsive-container-does-not-render
        return (
            <div>
                <div className='trafficStatsTitle'>Traffic over last hour</div>
                <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={this.props.trafficOverTime}
                        // margin helps to not cut-off first/last XAxis labels
                        margin={{ left: 30, right: 30 }}>
                        <XAxis dataKey="time" minTickGap={xTickGap}
                               tickFormatter={this.tickFormatter}/>
                        <YAxis/>
                        <Legend/>
                        <Tooltip/>
                        <CartesianGrid strokeDasharray="3"/>
                        <Line dataKey="inRcp" name="smtpIn" type="monotone"
                              stroke="#8884d8"
                              dot={false}
                              isAnimationActive={false}/>
                        <Line dataKey="outRcp" name="smtpOut" type="monotone"
                              stroke="#82ca9d"
                              dot={false}
                              isAnimationActive={false}/>
                    </LineChart>
                </ResponsiveContainer>
            </div>
        );
    }
}
